import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import { getBanners, getProducts, getServices, getSectionItems, getTestimonials, getLeafCategories } from '../api';
import { useSettings } from '../context/SettingsContext';
import PageWrapper from '../components/PageWrapper';
import BannerCarousel from '../components/BannerCarousel';
import ProductCard from '../components/ProductCard';
import ServiceCard from '../components/ServiceCard';
import CategoryCard from '../components/CategoryCard';
import Reveal from '../components/Reveal';

export default function Home() {
  const { settings } = useSettings();
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [features, setFeatures] = useState([]);
  const [process, setProcess] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [why, setWhy] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    getBanners(true).then(setBanners).catch(() => {});
    getLeafCategories(true).then(setCategories).catch(() => {});
    getProducts({ featured: true }).then((p) => setProducts(p.slice(0, 8))).catch(() => {});
    getServices({ featured: true }).then((s) => setServices(s.slice(0, 3))).catch(() => {});
    getSectionItems('features', true).then(setFeatures).catch(() => {});
    getSectionItems('process', true).then(setProcess).catch(() => {});
    getSectionItems('industries', true).then(setIndustries).catch(() => {});
    getSectionItems('why', true).then(setWhy).catch(() => {});
    getTestimonials(true).then(setTestimonials).catch(() => {});
  }, []);

  const h = settings?.home || {};
  const stats = settings?.aboutStats?.length ? settings.aboutStats : [];

  return (
    <PageWrapper>
      {/* Hero */}
      <section className="hero">
        {banners.length > 0 ? (
          <BannerCarousel banners={banners} />
        ) : (
          <div className="carousel">
            <div className="carousel__content">
              <h1>{settings?.heroTitle || 'Welcome'}</h1>
              <p>{settings?.heroSubtitle}</p>
              <Link to="/products" className="btn btn-primary">Explore Machines</Link>
            </div>
          </div>
        )}
      </section>

      {/* Feature highlights */}
      {features.length > 0 && (
        <section className="section-sm">
          <div className="container">
            <div className="features">
              {features.map((f, i) => (
                <Reveal key={f._id} delay={i * 0.08} className="feature glass">
                  <div className="feature__icon">{f.icon}</div>
                  <h4>{f.title}</h4>
                  <p>{f.subtitle}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shop by category */}
      {categories.length > 0 && (
        <section className="section" style={{ paddingTop: 40 }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <span className="eyebrow">{h.categoriesEyebrow || 'Shop by category'}</span>
              <h2 className="section-title">{h.categoriesTitle || 'Browse our range'}</h2>
            </div>
            <div className="grid grid-4">
              {categories.map((c, i) => <CategoryCard key={c._id} category={c} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className="section" style={{ paddingTop: categories.length > 0 ? 0 : 40 }}>
        <div className="container">
          <div className="between wrap" style={{ marginBottom: 30 }}>
            <div>
              <span className="eyebrow">{h.featuredEyebrow || 'Our machines'}</span>
              <h2 className="section-title">{h.featuredTitle || 'Featured Systems'}</h2>
            </div>
            <Link to="/products" className="btn btn-ghost">View all</Link>
          </div>
          {products.length > 0 ? (
            <div className="grid grid-4">
              {products.map((p, i) => <ProductCard key={p._id} product={p} index={i} />)}
            </div>
          ) : (
            <p className="muted">No featured products yet.</p>
          )}
        </div>
      </section>

      {/* Stats band */}
      {stats.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <Reveal className="stats-band">
              {stats.map((s, i) => (
                <div className="st" key={i}>
                  <div className="st__value">{s.value}</div>
                  <div className="st__label">{s.label}</div>
                </div>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {/* How it works */}
      {process.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <span className="eyebrow">{h.processEyebrow || 'How it works'}</span>
              <h2 className="section-title">{h.processTitle || 'From site survey to first wash'}</h2>
              {h.processSubtitle && <p className="section-sub" style={{ margin: '0 auto' }}>{h.processSubtitle}</p>}
            </div>
            <div className="process">
              {process.map((s, i) => (
                <Reveal key={s._id} delay={i * 0.1} className="pstep">
                  <div className="pstep__num">{i + 1}</div>
                  <h4>{s.title}</h4>
                  <p>{s.subtitle}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why choose us */}
      {why.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container why">
            <Reveal className="why__img">
              <img src={h.whyImage || settings?.aboutImage || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1000&q=80'} alt="Why" />
            </Reveal>
            <Reveal delay={0.1}>
              <span className="eyebrow">{h.whyEyebrow || 'Why Kortex'}</span>
              <h2 className="section-title">{h.whyTitle || 'Automation you can rely on'}</h2>
              <div className="why__list">
                {why.map((w) => (
                  <div className="why__item" key={w._id}>
                    <span className="why__check"><FiCheck /></span>
                    <div>
                      <h4>{w.title}</h4>
                      <p>{w.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn btn-primary mt-2">Learn more about us</Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Industries */}
      {industries.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <span className="eyebrow">{h.industriesEyebrow || 'Who we serve'}</span>
              <h2 className="section-title">{h.industriesTitle || 'Built for every wash business'}</h2>
            </div>
            <div className="industries">
              {industries.map((ind, i) => (
                <Reveal key={ind._id} delay={i * 0.08} className="ind">
                  <div className="ind__icon">{ind.icon}</div>
                  <h4>{ind.title}</h4>
                  <p>{ind.subtitle}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {services.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="between wrap" style={{ marginBottom: 30 }}>
              <div>
                <span className="eyebrow">{h.servicesEyebrow || 'What we do'}</span>
                <h2 className="section-title">{h.servicesTitle || 'Our Services'}</h2>
              </div>
              <Link to="/services" className="btn btn-ghost">All services</Link>
            </div>
            <div className="grid grid-3">
              {services.map((s, i) => <ServiceCard key={s._id} service={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <span className="eyebrow">{h.testimonialsEyebrow || 'Testimonials'}</span>
              <h2 className="section-title">{h.testimonialsTitle || 'Trusted by wash operators'}</h2>
            </div>
            <div className="grid grid-3">
              {testimonials.map((t, i) => (
                <Reveal key={t._id} delay={i * 0.1} className="tcard">
                  <div className="tcard__stars">{'★'.repeat(t.rating || 5)}</div>
                  <p className="tcard__quote">“{t.quote}”</p>
                  <div className="tcard__who">
                    <div className="tcard__avatar">{t.name[0]}</div>
                    <div>
                      <div className="tcard__name">{t.name}</div>
                      <div className="tcard__role">{t.role}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA band */}
      {h.ctaEnabled !== false && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <Reveal className="cta-band">
              <h2>{h.ctaTitle || 'Ready to automate your car wash?'}</h2>
              {h.ctaSubtitle && <p>{h.ctaSubtitle}</p>}
              {h.ctaButtonText && (
                /^https?:\/\//i.test(h.ctaButtonLink || '') ? (
                  <a href={h.ctaButtonLink} target="_blank" rel="noreferrer" className="btn btn-primary">
                    {h.ctaButtonText}
                  </a>
                ) : (
                  <Link to={h.ctaButtonLink || '/contact'} className="btn btn-primary">
                    {h.ctaButtonText}
                  </Link>
                )
              )}
            </Reveal>
          </div>
        </section>
      )}
    </PageWrapper>
  );
}
